import { AuthMethod } from '@infotech-soe/merps/types/session';
import { DigitalSignService } from '@infotech-soe/mia-ds-service';
import { IDigitalSignService } from '@infotech-soe/mia-ds-service/types';

import { AppDispatch } from '../..';
import IndexedDBObjectStorage from '../../../infrastructure/persistence/IndexedDBObjectStorage';
import ObjectStorage from '../../../infrastructure/persistence/ObjectStorage';
import IApiProvider from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { STORAGE_NAME } from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import { StoredSchemaSource } from '../../../infrastructure/schemas/storage/StoredSchemaSource';
import AuthService from '../../../services/auth/AuthService';
import { IAuthService } from '../../../services/auth/IAuthService';
import CategoryService from '../../../services/category/CategoryService';
import ICategoryService from '../../../services/category/ICategoryService';
import ILocalConfigurationService from '../../../services/configuration/ILocalConfigurationService';
import LocalConfigurationService from '../../../services/configuration/LocalConfigurationService';
import ConnectionService from '../../../services/connection/ConnectionService';
import { IConnectionService } from '../../../services/connection/IConnectionService';
import {
  getAllContentEntityIds,
  getContentEntity,
} from '../../selectors/content';
import connection from '../../slices/connection';
import content, { ContentEntityType } from '../../slices/content';
import error from '../../slices/error';
import menu from '../../slices/menu';
import notifications from '../../slices/notifications';
import schema from '../../slices/schema';
import session, { AuthResult } from '../../slices/session';
import systemNotifications from '../../slices/systemNotifications';
import userConfig from '../../slices/userConfig';
import {
  apiCall,
  createAppListener,
  onceInit,
  postDispatch,
} from '../listenerHelpers';

const LOGOUT_OR_RELOAD =
  '\nПерезавантажте сторінку або переувійдіть в затосунок.';

const sessionListener = createAppListener();

const apiProvider: IApiProvider = new MerpsApiProvider();
const storage: ObjectStorage<StoredSchemaSource> = new IndexedDBObjectStorage();
const localConfigurationService: ILocalConfigurationService =
  new LocalConfigurationService();
const connectionService: IConnectionService = new ConnectionService();
const categoryService: ICategoryService = new CategoryService();
const signService: IDigitalSignService = new DigitalSignService();
const authService: IAuthService = new AuthService();

const postSignIn = (application: string | null, dispatch: AppDispatch) => {
  if (application) {
    dispatch(menu.get({ application }));
  }

  dispatch(notifications.subscribe());
  dispatch(notifications.get());
  dispatch(systemNotifications.get());
  dispatch(userConfig.getGeneral());
};

sessionListener.startListening(
  onceInit((_state, dispatch) => {
    storage.open(STORAGE_NAME);
    apiProvider.rpc.onReconnectAuthFailed.addListener(() => {
      dispatch(connection.connectAndSignIn({ isReconnection: true }));
    });
  })
);

sessionListener.startListening(
  apiCall(
    {
      request: session.signIn,
      success: session.signInSuccess,
      failure: session.signInFailure,
    },
    async (payload, _state, dispatch) => {
      const { application, ...data } = payload;

      const authData = await authService.signIn(data, application);

      const newCrc = await authService.getSchemaCrc();
      const currtCrc = await storage.get('crc');

      if (newCrc !== currtCrc?.source) {
        dispatch(schema.purgeCache());
        await storage.set('crc', { source: newCrc });
      }

      const projectInfo = await authService.getProjectInfo();

      if (
        payload.type === AuthMethod.LOGIN ||
        payload.type === AuthMethod.WINDOWS_AD
      ) {
        localConfigurationService.storeDevCredentials(
          payload.userName,
          payload.password,
          application
        );
      }

      return {
        login: authData.userName ?? '',
        application,
        type: data.type,
        ...authData,
        projectInfo,
      };
    }
  )
);

sessionListener.startListening(
  apiCall(
    {
      request: session.reconnection,
      success: session.reconnectionSuccess,
      failure: session.reconnectionFailure,
    },
    async ({ application, ...data }, _state, dispatch) => {
      try {
        await authService.signIn(data, application);
      } catch (err: any) {
        dispatch(
          error.showNotification({
            name: err.name,
            id: err.id ?? null,
            code: err.code,
            message: err.message,
            stack: err.stack,
            info: err.info + LOGOUT_OR_RELOAD,
            logout: false,
          })
        );
        return { authenticated: AuthResult.IN_PROGRESS };
      }
      return { authenticated: AuthResult.YES };
    }
  )
);

sessionListener.startListening(
  postDispatch(
    session.signInSuccess,
    ({ application, type }, _state, dispatch) => {
      localConfigurationService.storeLastUsedAuthMethod(type);
      postSignIn(application, dispatch);
    }
  )
);

sessionListener.startListening(
  apiCall(
    {
      request: session.signOut,
      success: session.signOutSuccess,
      failure: session.signOutFailure,
    },
    async (_payload, state, dispatch) => {
      for (const contentId of getAllContentEntityIds(state)) {
        const { type, content: contentData } = getContentEntity(
          state,
          contentId
        );
        const datasets = state.dataset;

        if (type === ContentEntityType.CATEGORY) {
          const categoryState = state.category[contentData];
          await categoryService.saveCategory(categoryState, datasets);
        }
      }
      dispatch(systemNotifications.clear());

      await authService.signOut();
      const connected = await connectionService.connect();
      dispatch(connection.setConnectionStatus({ connected }));

      sessionStorage.clear();
      signService.clearStoredCredentials();
    }
  )
);

sessionListener.startListening(
  postDispatch(
    session.signOutSuccess,
    (_payload, _state, dispatch, prevState) => {
      for (const contentId of getAllContentEntityIds(prevState)) {
        dispatch(content.close({ id: contentId, save: false }));
      }
      dispatch(session.loadFeatures());
    }
  )
);

sessionListener.startListening(
  postDispatch(
    session.bindStateToExistingSession,
    ({ application }, _state, dispatch) => {
      postSignIn(application, dispatch);
    }
  )
);

sessionListener.startListening(
  apiCall(
    {
      request: session.loadFeatures,
      success: session.loadFeaturesSuccess,
      failure: session.loadFeaturesFailure,
    },
    async () => authService.getFeaturesList()
  )
);

export default sessionListener.middleware;
